import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import { classNames } from "primereact/utils";
import { useContext, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { getErrorTitle } from "../../../constants/constants";
import { RouterAddress } from "../../../constants/RouterAddress";
import { DropDownOption } from "../../../models/primereact";
import ContactCard from "../../../components/general/contactCard";
import Unauthorized from "../../../shared/pages/Unauthorized";
import { confirmDialog } from "primereact/confirmdialog";
import { Tag } from "primereact/tag";
import { useResponsive } from "../../../responsive/ResponsiveProvider";

const Support = () => {
  const toast = useRef<Toast>(null);
  const mobile = useResponsive().isMobile;
  const navigate = useNavigate();
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [userId, setUserId] = useState<string>(authStore.getUserId());
  const [userRoleType, setUserRoleType] = useState<string | null>(authStore.getUserRoleType());
  const [customerFeedbacks, setCustomerFeedbacks] = useState<any[]>([]);
  const [customerFeedbacksLoading, setCustomerFeedbacksLoading] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<any | null>(null);
  const [feedbackMessages, setFeedbackMessages] = useState<any[]>([]);
  const [feedbackMessagesLoading, setFeedbackMessagesLoading] = useState(false);
  const [feedbackReply, setFeedbackReply] = useState("");
  const [feedbackReplyLoading, setFeedbackReplyLoading] = useState(false);
  const BUG: string = "Bug";

  const FEEDBACK_TYPES: DropDownOption[] = [
    { name: BUG, code: BUG },
    { name: "Feature Request", code: "Feature Request" },
    { name: "General Feedback", code: "General Feedback" },
  ];

  const defaultValues = {
    feedbackType: FEEDBACK_TYPES[0],
    feedbackComment: '',
  }

  const { control, formState: { errors }, handleSubmit, reset, watch } = useForm({ defaultValues });
  const selectedFeedbackType = watch("feedbackType");
  const selectedFeedbackTypeCode = selectedFeedbackType?.code || selectedFeedbackType?.name || "";
  const feedbackCommentPlaceholder = selectedFeedbackTypeCode === BUG
    ? "Describe what you were trying to do, what happened instead, and which page, browser, or device was involved."
    : selectedFeedbackTypeCode === "Feature Request"
      ? "Describe the feature or workflow you would like, who it would help, and how you would expect it to work."
      : "Share your feedback, question, or suggestion with any helpful context.";

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  useEffect(() => {
    const fetchProfileData = async () => {
      setLoading(true);
      try {
        await backendAPIStore.init();
        const authenticated = await authStore.isUserLoggedIn();
        if (authenticated) {
          setUserId(authStore.getUserId());
          setUserRoleType(authStore.getUserRoleType());
          const userDetails = await backendAPIStore.getUserProfile(authStore.getUserId());
          if (!userDetails) {
            navigate(RouterAddress.LOGIN_PAGE);
            return;
          }
          setCustomerFeedbacksLoading(true);
          const feedbackResponse = await backendAPIStore.listCustomerFeedback();
          setCustomerFeedbacks(Array.isArray(feedbackResponse) ? feedbackResponse : []);
        }
      } catch (error: any) {
        setErrorMessage(error.message);
        toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: "Failed to fetch user details: " + error.message, life: 3000 });
      } finally {
        setCustomerFeedbacksLoading(false);
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [userId]);

  const formatFeedbackDate = (value: any) => value
    ? new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Los_Angeles",
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value))
    : "N/A";

  const feedbackTypeLabel = (feedback: any) => feedback?.feedbackType?.name
    || feedback?.feedbackType?.code
    || (typeof feedback?.feedbackType === "string" ? feedback.feedbackType : "General");

  const sortedCustomerFeedbacks = [...customerFeedbacks].sort((first: any, second: any) =>
    String(second?.lastMessageAt || second?.createdAt || "").localeCompare(
      String(first?.lastMessageAt || first?.createdAt || ""),
    )
  );

  const openFeedbackDialog = async (feedback: any) => {
    setSelectedFeedback(feedback);
    setFeedbackReply("");
    setFeedbackMessages([]);
    setFeedbackMessagesLoading(true);
    try {
      const response = await backendAPIStore.listCustomerFeedbackMessages(String(feedback?.feedbackId || ""));
      setFeedbackMessages(Array.isArray(response?.messages) ? response.messages : []);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to load request history.", life: 3000 });
    } finally {
      setFeedbackMessagesLoading(false);
    }
  };

  const submitFeedbackReply = async () => {
    const message = feedbackReply.trim();
    if (!selectedFeedback?.feedbackId || !message || feedbackReplyLoading) {
      return;
    }
    setFeedbackReplyLoading(true);
    try {
      await backendAPIStore.replyToCustomerFeedback(selectedFeedback.feedbackId, message);
      const [threadResponse, feedbackResponse] = await Promise.all([
        backendAPIStore.listCustomerFeedbackMessages(selectedFeedback.feedbackId),
        backendAPIStore.listCustomerFeedback(),
      ]);
      setFeedbackMessages(Array.isArray(threadResponse?.messages) ? threadResponse.messages : []);
      setCustomerFeedbacks(Array.isArray(feedbackResponse) ? feedbackResponse : []);
      setSelectedFeedback((current: any) => (Array.isArray(feedbackResponse)
        ? feedbackResponse.find((item: any) => item?.feedbackId === current?.feedbackId) || current
        : current));
      setFeedbackReply("");
      toast.current?.show({ severity: "success", summary: "Reply sent", detail: "Your reply was added to the request.", life: 2500 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to send reply.", life: 3000 });
    } finally {
      setFeedbackReplyLoading(false);
    }
  };

  const onSubmit = async (formData: any) => {
    if (formData.feedbackType.name === BUG) {
      confirmDialog({
        message: <p>AFH Hub is not fully tested in Safari browser. <br /> If you are using Safari, we recommend switching to Chrome or Firefox for a better experience. <br /> Do you still want to submit the feedback?</p>,
        header: 'Browser Compatibility Warning',
        icon: 'pi pi-exclamation-triangle',
        accept: async () => {
          await submitFeedback(formData);
        },
        reject: () => {
          // Do nothing if the user rejects the confirmation dialog
        }
      });
    } else {
      await submitFeedback(formData);
    }
  };

  const submitFeedback = async (formData: any) => {
    setLoading(true);
    let tempData: any = {};
    tempData.userId = userId;
    tempData.feedbackType = formData.feedbackType;
    tempData.feedbackComment = formData.feedbackComment;

    try {
      await backendAPIStore.addCustomerFeedback(tempData);
      const feedbackResponse = await backendAPIStore.listCustomerFeedback();
      setCustomerFeedbacks(Array.isArray(feedbackResponse) ? feedbackResponse : []);
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Feedback submitted successfully.', life: 3000 });
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
      setErrorMessage("Failed to add feedback.");
    } finally {
      reset();
      setLoading(false);
    }
  };

  const titleTemplate = (title) => {
    return (
      <div className="flex flex-wrap align-items-center justify-content-between gap-2">
        <span className="text-xl text-900">{title}</span>
      </div>
    );
  };

  if (userRoleType && !userId) {
    return <Unauthorized />;
  }

  const sectionCardStyle = {
    border: "1px solid #e3edf5",
    boxShadow: "0 14px 32px rgba(24, 93, 162, 0.08)",
    borderRadius: "16px",
  } as const;

  return (
    <div className="container-fluid p-3">
      <Toast ref={toast} />
      <div style={{ width: "100%", margin: "0 auto" }}>
        <div className="mb-4">
          <h2 className="m-0">Support</h2>
          <div className="mt-2" style={{ color: "#596F80" }}>
            Reach our team, report issues, and share feature ideas to help improve AFH Hub.
          </div>
        </div>

        <Card
          title={titleTemplate("Report a Bug, Feature Request, or Suggestion")}
          className="w-full mt-3"
          style={{ ...sectionCardStyle, width: "100%" }}
        >
          <form>
            <div className="grid align-items-start">
              <div className="col-12 lg:col-7">
                <div style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "16px", padding: "1.25rem" }}>
                  <div className="mb-4">
                    <h4 className="m-0" style={{ color: "#185DA2" }}>Feedback Details</h4>
                    <div className="mt-2" style={{ color: "#64748b" }}>
                      Choose the type of feedback and include as much detail as you can.
                    </div>
                  </div>

                  <div className="field mb-4">
                    <label htmlFor="feedbackType" className="mb-2 block">Feedback Type</label>
                    <span className="p-float-label">
                      <Controller name="feedbackType" control={control} rules={{
                        required: 'Type is required.',

                      }} render={({ field, fieldState }) => (
                        <Dropdown id={field.name} value={field.value} optionLabel="name" style={{ width: '100%' }}
                          onChange={(e) => field.onChange(e.value)} options={FEEDBACK_TYPES} className={classNames({ 'p-invalid': fieldState.invalid })} />
                      )} />
                    </span>
                    {getFormErrorMessage('feedbackType')}
                  </div>

                  <div className="field mb-2">
                    <span className="p-float-label">
                      <Controller name="feedbackComment" control={control} rules={{
                        required: 'Comment is required.',
                        maxLength: { value: 1000, message: 'Comment must be at most 1000 characters long.' }
                      }} render={({ field, fieldState }) => (
                        <InputTextarea key={`feedback-comment-${selectedFeedbackTypeCode}`} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} rows={6} cols={50}
                          placeholder={feedbackCommentPlaceholder}
                          style={{ width: '100%', padding: mobile ? '1rem' : '0.75rem' }} />
                      )} />
                      <label htmlFor="feedbackComment" className={classNames({ 'p-error': errors.feedbackComment })}>Feedback Comment</label>
                    </span>
                    {getFormErrorMessage('feedbackComment')}
                  </div>

                  <div className="flex justify-content-end mt-3">
                    <Button
                      type="button"
                      severity="warning"
                      label="Submit Feedback"
                      rounded
                      loading={loading}
                      onClick={() => handleSubmit(onSubmit)()}
                    />
                  </div>
                </div>
              </div>

              <div className="col-12 lg:col-5">
                <div style={{ background: "linear-gradient(180deg, #fffaf0 0%, #ffffff 100%)", border: "1px solid #fde68a", borderRadius: "16px", padding: "1.25rem", marginBottom: "1rem" }}>
                  <h4 className="m-0" style={{ color: "#9a3412" }}>Contact Us</h4>
                  <div className="mt-3" style={{ color: "#596F80", lineHeight: 1.7 }}>
                    <ContactCard />
                  </div>
                </div>
              </div>
            </div>

            {errorMessage && <div className="p-error mt-3">{errorMessage}</div>}
          </form>
        </Card>
        <Card
          title={titleTemplate("Submitted Requests")}
          className="w-full mt-3"
          style={{ ...sectionCardStyle, width: "100%" }}
        >
          <DataTable
            value={sortedCustomerFeedbacks}
            loading={customerFeedbacksLoading}
            size="small"
            stripedRows
            emptyMessage="You have not submitted any requests yet."
            responsiveLayout="scroll"
          >
            <Column field="createdAt" header="Submitted" body={(row: any) => formatFeedbackDate(row?.createdAt)} />
            <Column header="Type" body={(row: any) => <Tag value={feedbackTypeLabel(row)} severity="info" rounded />} />
            <Column
              header="Latest message"
              style={{ minWidth: mobile ? "22rem" : "34rem" }}
              body={(row: any) => (
                <div style={{ minWidth: mobile ? "20rem" : "32rem", whiteSpace: "normal", overflowWrap: "anywhere" }}>
                  <div style={{ color: "#0f172a", lineHeight: 1.45 }}>
                    {row?.lastMessagePreview || row?.feedbackComment || "No message"}
                  </div>
                  <small style={{ color: "#64748b" }}>
                    {formatFeedbackDate(row?.lastMessageAt || row?.createdAt)}
                  </small>
                </div>
              )}
            />
            <Column header="Action" body={(row: any) => <Button type="button" label="Open" icon="pi pi-comments" size="small" rounded outlined onClick={() => void openFeedbackDialog(row)} />} />
          </DataTable>
        </Card>
      </div>
      <Dialog
        visible={Boolean(selectedFeedback)}
        onHide={() => setSelectedFeedback(null)}
        modal
        style={{ width: "min(44rem, 96vw)" }}
        header={selectedFeedback ? `Support Request · ${feedbackTypeLabel(selectedFeedback)}` : "Support Request"}
        footer={(
          <div className="flex gap-2 justify-content-end">
            <Button type="button" label="Close" text rounded onClick={() => setSelectedFeedback(null)} />
            <Button type="button" label="Reply" icon="pi pi-send" severity="warning" rounded loading={feedbackReplyLoading} disabled={!feedbackReply.trim()} onClick={() => void submitFeedbackReply()} />
          </div>
        )}
      >
        <div className="flex flex-column gap-3">
          <div className="flex gap-2 flex-wrap align-items-center">
            <Tag value={feedbackTypeLabel(selectedFeedback)} severity="info" rounded />
          </div>
          <div style={{ maxHeight: "22rem", overflowY: "auto" }}>
            {feedbackMessagesLoading ? <div style={{ color: "#64748b" }}>Loading conversation...</div> : feedbackMessages.map((item: any) => (
              <div key={item.messageId} className="mb-3" style={{ border: "1px solid #e2e8f0", borderRadius: "12px", padding: "0.8rem", background: item.senderRole === "admin" ? "#eff6ff" : "#fff7ed" }}>
                <div className="flex justify-content-between gap-2 flex-wrap" style={{ fontSize: "0.8rem", color: "#475569" }}>
                  <strong>{item.senderRole === "admin" ? "AFH Hub Admin" : "You"}</strong>
                  <span>{formatFeedbackDate(item.createdAt)}</span>
                </div>
                <div className="mt-2" style={{ whiteSpace: "pre-wrap", color: "#0f172a" }}>{item.message}</div>
                {item.emailNotification?.status && item.emailNotification.status !== "not_required" ? <div className="mt-2" style={{ fontSize: "0.75rem", color: "#64748b" }}>Email: {item.emailNotification.status}</div> : null}
              </div>
            ))}
          </div>
          <InputTextarea value={feedbackReply} onChange={(event) => setFeedbackReply(event.target.value)} rows={4} maxLength={4000} autoResize placeholder="Write a reply..." className="w-full" />
        </div>
      </Dialog>
    </div >
  );
}

export default Support;
